import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authService } from '../services/authService';

const Auth = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await authService.login(formData.email, formData.password);
      toast.success('¡Bienvenido de vuelta!');
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error en autenticación';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAsGuest = () => {
    toast.success('Continuando como visitante');
    navigate('/');
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 via-white to-blue-50 pb-20 md:pb-0">
      <div className="min-h-screen flex items-center justify-center p-4 md:p-8">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-6 md:mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_worship-tracks-5/artifacts/twg4kldp_LOGO%20Iglesia%202.png"
              alt="Tu Iglesia App"
              className="w-40 md:w-48 mx-auto mb-4 md:mb-6"
            />
            <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">
              Bienvenido de vuelta
            </h1>
            <p className="text-sm md:text-base text-gray-500">
              Inicia sesión con tu cuenta de Tu Iglesia App
            </p>
          </div>

          {/* Auth Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Registration Info Banner */}
            <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs md:text-sm text-blue-800 font-medium text-center">
                ¿No tienes cuenta? Regístrate primero en la aplicación principal de{' '}
                <a 
                  href="https://www.tuiglesia.app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold text-[#00CCFF] hover:text-[#056A84] underline transition-colors duration-200"
                >
                  Tu Iglesia App
                </a>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 md:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00CCFF] transition-all duration-200 text-sm md:text-base"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-12 pr-12 py-3 md:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00CCFF] transition-all duration-200 text-sm md:text-base"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 md:py-4 bg-gradient-to-r from-[#00CCFF] to-[#056A84] text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
              >
                {loading ? 'Cargando...' : 'Iniciar Sesión'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">O continúa como</span>
              </div>
            </div>

            {/* Guest Access */}
            <button
              type="button"
              onClick={handleContinueAsGuest}
              className="w-full py-3 text-gray-600 hover:text-[#00CCFF] font-semibold transition-colors duration-200 text-sm md:text-base"
            >
              Continuar como visitante
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
